import moment from "moment";
import {
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";

import conf from "config-dlm";
import ErrorChip from "components/utils/ErrorChip";
import JsonModal from "components/utils/Json";
import { useAuth } from "components/auth/AuthProvider";
import EmptyTableRow from "./Empty";
import { ServiceSchema } from "hooks/pavm/services";
import RequestWithSchedule from "components/utils/RequestWithSchedule";
import { usePavmRequestServiceRestart } from "hooks/pavm/request";
import { Snapshot, ProjectConfigSchema } from "hooks/pavm/base";

const ServiceDetail = (props: {
  uri: string;
  host: string;
  service: ServiceSchema;
  updatedAt: Date | string | undefined;
}) => {
  const { host, service, updatedAt } = props;
  const { name, data, error } = service;

  const restart = usePavmRequestServiceRestart();

  const handleRestart = (triggerAt: number, reason: string) => {
    restart.mutate({ host, service: name, reason, triggerAt });
  };

  const state = !!error ? (
    <ErrorChip size="small" label={data?.core?.active} />
  ) : (
    <Chip
      size="small"
      color="success"
      label={data?.core?.active}
      icon={<CheckCircle />}
    />
  );

  return (
    <TableRow key={`${service}-info`} sx={conf.LAST_CHILD_NO_BORDER}>
      <TableCell />
      <TableCell component="th" scope="row" align="right">
        {name}
      </TableCell>
      <TableCell align="right">{state}</TableCell>
      <TableCell align="right">{data?.core?.tasks ?? "--"}</TableCell>
      <TableCell align="right">{data?.core?.memory ?? "--"}</TableCell>
      <TableCell align="right">{data?.core?.cpu ?? "--"}</TableCell>
      <TableCell align="right">{data?.core?.pid ?? "--"}</TableCell>
      <TableCell align="right">{moment(updatedAt).fromNow()}</TableCell>
      <TableCell align="right">
        <JsonModal data={data?.raw} type="Service" title={name} />
      </TableCell>
      <TableCell align="right">
        <RequestWithSchedule
          type="Service Restart"
          size="small"
          details={name}
          onSubmit={handleRestart}
          onSuccess={restart.isSuccess}
        />
      </TableCell>
    </TableRow>
  );
};

const ServiceRow = ({
  open,
  config,
  data,
  updatedAt,
}: {
  open: boolean;
  config: ProjectConfigSchema;
  data: Snapshot["services"];
  updatedAt: Date | string | undefined;
}) => {
  const { user } = useAuth();

  const host = config.host;
  const uri = user.project.uri;

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          theme.palette.mode === "light" ? "primary.light" : "primary.dark",
      }}
    >
      <TableCell colSpan={10}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Table size="small" aria-label="disk-info">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "text.secondary" }}>
                  <b>Services</b>
                </TableCell>
                {conf.COLUMNS.SERVICES.map((column: string) => (
                  <TableCell key={column} align="right">
                    <b>{column}</b>
                  </TableCell>
                ))}

                <TableCell key={"request-service-restart"} align="right">
                  Restart
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.length === 0 ? (
                <EmptyTableRow colSpan={10} type="services" />
              ) : (
                data.map((service) => (
                  <ServiceDetail
                    key={service.name}
                    host={host}
                    service={service}
                    updatedAt={updatedAt}
                    uri={uri}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export default ServiceRow;
